/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core'
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http'
import {
    BehaviorSubject,
    catchError,
    Observable,
    Subject,
    Subscription,
    switchMap,
    throwError,
} from 'rxjs'
import { AuthService } from '../auth/auth.service'
import { AuthenticationLoginResponseModel } from '../../models/authenticationLoginResponse.model'
import { TranslateService } from '@ngx-translate/core'
import { SILENT_ERROR_STATUSES } from './http-context-tokens'

/**
 * Intercepts HTTP requests to handle error responses, particularly unauthorized access (401) and forbidden access (403).

 * This interceptor implements token refresh logic for unauthorized errors:
 * - If a 401 error occurs and a refresh is not currently in progress:
 - Triggers a refresh token request using the `AuthService`.
 - Sets a flag (`isRefreshing`) to prevent multiple concurrent refreshes.
 - Emits an event (`accessTokenError$`) to signal the refresh process.
 - Waits for the refresh to complete using a `waitNewTokens` method.
 - If the refresh is successful:
 - Updates the auth result with the new tokens.
 - Clones the intercepted request with a new authorization header.
 - Retries the request with the refreshed token.
 - If the refresh fails:
 - Logs out the user using the `AuthService`.
 - Re-throws the original error.
 - If a 401 error occurs while a refresh is already in progress:
 - Waits for the ongoing refresh to complete using the `waitNewTokens` method.
 - If the refresh is successful:
 - Updates the auth result with the new tokens.
 - Clones the intercepted request with a new authorization header.
 - Retries the request with the refreshed token.
 - If the refresh fails (after the initial attempt):
 - Logs out the user using the `AuthService`.
 - Re-throws the original error.
 - If a 403 error occurs (forbidden access):
 - Logs out the user using the `AuthService`.

 * For other error responses, the interceptor simply re-throws the error message.

 * @Injectable()
 * @export class ErrorInterceptor implements HttpInterceptor
 */
@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
    /**
     * BehaviorSubject to indicate if an access token error has occurred.
     *
     * @private
     * @static
     */
    private static accessTokenError$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)

    /**
     * Flag to indicate if a token refresh is currently in progress.
     *
     * @private
     * @static
     */
    private static isRefreshing = false

    /**
     * Constructs a new TokenErrorService instance.
     *
     * @param _authService The AuthService instance.
     */
    constructor(
        private _authService: AuthService,
        private translate: TranslateService
    ) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        // Fix login with wrong credentials
        // Check if the current request is a login request
        const isLoginRequest = request.url.includes('/Login');


        return next.handle(request).pipe(
            catchError((err) => {
                /** If a 401 Unauthorized error occurs: */
                if (err.status === 401) {

                    // If an error 401 occour and the current request is a login request thwn throw an error
                    if (isLoginRequest) {
                        
                        // return translated error message
                        return this.translate.get('INVALID_CREDENTIALS').pipe(
                            switchMap((translatedMessage: string) => {
                              return throwError(() => new Error(translatedMessage));
                            })
                          );

                    }

                    if (!ErrorInterceptor.isRefreshing) {
                        ErrorInterceptor.isRefreshing = true
                        ErrorInterceptor.accessTokenError$.next(true)

                        return this._authService.refreshToken().pipe(
                            switchMap((data: any) => {
                                ErrorInterceptor.isRefreshing = false
                                ErrorInterceptor.accessTokenError$.next(false)
                                this._authService.setAuthResult(data)

                                const authResult: AuthenticationLoginResponseModel | null =
                                    this._authService.getAuthResult()
                                if (authResult != null) {
                                    const cloned = request.clone({
                                        headers: request.headers.set(
                                            'Authorization',
                                            'Bearer ' + authResult.token
                                        ),
                                    })
                                    return next.handle(cloned)
                                } else {
                                    return next.handle(request)
                                }
                            }),
                            catchError((refreshError) => {
                                ErrorInterceptor.isRefreshing = false
                                ErrorInterceptor.accessTokenError$.next(false)
                                this._authService.logout()
                                return throwError(refreshError)
                            })
                        )
                    } else {
                        /** If a refresh is already in progress: */
                        return this.waitNewTokens().pipe(
                            switchMap(() => {
                                const authResult: AuthenticationLoginResponseModel | null =
                                    this._authService.getAuthResult()
                                if (authResult != null) {
                                    const newRequest = request.clone({
                                        setHeaders: {
                                            Authorization: `Bearer ${authResult.token}`,
                                        },
                                    })
                                    return next.handle(newRequest)
                                } else {
                                    return next.handle(request)
                                }
                            })
                        )
                    }
                } else if (err.status === 403) {
                    /** If a 403 Forbidden error occurs: */
                    this._authService.logout()
                }

                /**
                 * If this request opted out of error notifications for this
                 * status, forward the original HttpErrorResponse untouched so
                 * the outer ApiInterceptor can recognize it and stay silent.
                 */
                if (request.context.get(SILENT_ERROR_STATUSES).includes(err.status)) {
                    return throwError(() => err)
                }

                /** For other error statuses, re-throw the error */
                /**
                 * If defined use err.error.error or err.error.title or err.error.message; several
                 * backend actions return BadRequestObjectResult(string) — a bare JSON string body,
                 * not an {error/title/message} object — so err.error itself IS the message in that
                 * case. Without this check the real backend text was always skipped in favour of
                 * err.statusText (unreliable — e.g. reads "OK" for some HTTP/2 responses).
                 */
                const bodyMessage =
                    typeof err.error === 'string'
                        ? err.error
                        : err.error?.error || err.error?.title || err.error?.message
                const error = bodyMessage || err.statusText

                return throwError(error)
            })
        )
    }

    /**
     * Creates an observable that waits for the `accessTokenError$` BehaviorSubject to emit `false`, indicating that a token refresh has completed.

     * This method is used to handle scenarios where a 401 (Unauthorized) error occurs during an HTTP request.
     * If a refresh is already in progress (`isRefreshing` flag), this method creates an observable that waits for the refresh to finish.

     * @private
     * @returns {Observable<any>} An observable that emits a single value (`true`) when the token refresh is complete.
     */
    private waitNewTokens(): Observable<any> {
        const subject = new Subject<any>()
        const waitToken$: Subscription = ErrorInterceptor.accessTokenError$.subscribe(
            (error: boolean) => {
                if (!error) {
                    subject.next(true)
                    subject.complete()
                    waitToken$.unsubscribe()
                }
            }
        )
        return subject.asObservable()
    }
}
